(() => {
  let dispose = () => {};

  function initialize() {
    dispose();
    const root = document.documentElement;
    const hero = document.querySelector('main > .shopify-section:first-child [data-mr-brand-hero]');
    const header = document.querySelector('[data-mr-header]');
    const source = hero?.querySelector('.mr-hero-wordmark__image');
    const target = header?.querySelector('.header__logo-image');
    if (!hero || !header || !source || !target) return;
    // Native dialogs move outside x-header while open; keep their node references.
    const overlays = Array.from(header.querySelectorAll('header-search, header-sidebar'));

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const signal = controller.signal;
    const morph = document.createElement('div');
    morph.className = 'mr-brand-morph';
    morph.setAttribute('aria-hidden', 'true');
    const ink = document.createElement('div');
    ink.className = 'mr-brand-ink';
    ink.setAttribute('aria-hidden', 'true');

    const light = source.cloneNode();
    const dark = source.cloneNode();
    light.className = 'mr-brand-morph__light';
    dark.className = 'mr-brand-morph__dark';
    light.alt = '';
    dark.alt = '';
    morph.append(light);
    ink.append(dark);
    document.body.append(morph, ink);

    let frame = 0;
    let geometry = null;
    let needsMeasure = true;
    let motion = [];
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const mix = (a, b, amount) => a + (b - a) * amount;
    const smooth = (value) => value * value * (3 - 2 * value);

    function pose(scroll) {
      const progress = clamp((scroll - geometry.start) / (geometry.end - geometry.start), 0, 1);
      const eased = smooth(progress);
      const scale = mix(1, geometry.targetWidth / geometry.width, eased);
      const x = mix(geometry.x, geometry.targetX, eased);
      const startY = geometry.y - geometry.start;
      const travel = Math.max(1, startY - geometry.targetY);
      const slope = clamp((geometry.end - geometry.start) / travel, 0, 3);
      const verticalProgress = eased + slope * progress * (1 - progress) ** 2;
      const y = progress === 0 ? geometry.y - scroll : mix(startY, geometry.targetY, verticalProgress);
      return { transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`, progress, eased };
    }

    function cancelMotion() {
      motion.forEach((animation) => animation.cancel());
      motion = [];
    }

    function createMotion() {
      cancelMotion();
      if (reduced.matches || !('ScrollTimeline' in window)) return;
      // Pixel ranges keep the path independent of products/images loading below
      // the hero. Both layers use the same native timeline, without a logo swap.
      // https://developer.chrome.com/docs/css-ui/scroll-driven-animations
      try {
        const timeline = new ScrollTimeline({ source: root, axis: 'block' });
        const positions = [0, geometry.start];
        for (let step = 1; step <= 64; step++) {
          positions.push(mix(geometry.start, geometry.end, step / 64));
        }
        const keyframes = positions.map((scroll) => ({
          offset: scroll / geometry.end,
          transform: pose(scroll).transform
        }));
        const options = { timeline, rangeStart: '0px', rangeEnd: `${geometry.end}px`, fill: 'both', easing: 'linear' };
        motion.push(morph.animate(keyframes, options));
        motion.push(dark.animate(keyframes, options));
      } catch {
        cancelMotion();
      }
    }

    function render() {
      frame = 0;
      if (!source.isConnected || !target.isConnected) return;
      const destination = target.getBoundingClientRect();
      const headerRect = header.getBoundingClientRect();
      let measured = false;
      if (needsMeasure) {
        const start = source.getBoundingClientRect();
        const stickySection = header.closest('.shopify-section--header');
        const stickyTop = parseFloat(stickySection && getComputedStyle(stickySection).top) || 0;
        const restingHeaderTop = stickyTop + destination.top - headerRect.top;
        const sourceY = start.top + window.scrollY;
        const latestDelay = Math.max(0, sourceY - restingHeaderTop - 96);
        const delay = Math.min(Math.max(160, hero.clientHeight * .4), latestDelay);
        const next = {
          x: start.left, y: sourceY, width: start.width, height: start.height,
          targetX: destination.left, targetY: restingHeaderTop, targetWidth: destination.width,
          start: delay, end: Math.max(delay + 220, sourceY - restingHeaderTop)
        };
        measured = JSON.stringify(next) !== JSON.stringify(geometry);
        geometry = next;
        needsMeasure = false;
      }
      if (!geometry.width || !geometry.height || !destination.width) return;

      const scroll = Math.max(0, window.scrollY);
      const current = pose(scroll);
      const navigationOpen = overlays.some((node) => node.hasAttribute('open')) ||
        Array.from(header.querySelectorAll('.header__primary-nav details[open]')).some((node) => node.getBoundingClientRect().width > 0);
      const engaged = header.classList.contains('is-engaged');
      morph.style.width = `${geometry.width}px`;
      morph.style.height = `${geometry.height}px`;
      dark.style.width = `${geometry.width}px`;
      dark.style.height = `${geometry.height}px`;
      if (!motion.length || measured) {
        morph.style.transform = current.transform;
        dark.style.transform = current.transform;
      }
      if (measured) createMotion();
      ink.style.setProperty('--mr-header-top', `${Math.max(0, headerRect.top)}px`);
      ink.style.setProperty('--mr-header-bottom', `${Math.max(0, headerRect.bottom)}px`);

      root.classList.add('mr-brand-ready');
      root.classList.toggle('mr-brand-reduced', reduced.matches);
      root.classList.toggle('mr-brand-navigation-open', navigationOpen);
      root.classList.toggle('mr-brand-docked', current.progress >= 1);
      header.style.setProperty('--mr-glass-opacity', engaged ? '.40' : String(mix(.015, .40, current.eased)));
      header.style.setProperty('--mr-glass-blur', `${engaged ? 20 : mix(8, 20, current.eased)}px`);
      header.style.setProperty('--mr-glass-saturation', `${engaged ? 140 : mix(150, 140, current.eased)}%`);
    }

    function schedule() {
      if (!frame) frame = requestAnimationFrame(render);
    }

    function measure() {
      needsMeasure = true;
      schedule();
    }

    function onScroll() {
      if (motion.length || reduced.matches) schedule();
      else render();
    }

    const resize = new ResizeObserver(measure);
    resize.observe(hero);
    resize.observe(source);
    resize.observe(target);
    const state = new MutationObserver(schedule);
    state.observe(header, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
    overlays.forEach((node) => state.observe(node, { attributes: true, attributeFilter: ['open'] }));
    window.addEventListener('scroll', onScroll, { passive: true, signal });
    window.addEventListener('resize', measure, { passive: true, signal });
    window.addEventListener('pageshow', measure, { signal });
    header.addEventListener('mr:header-state', schedule, { signal });
    source.addEventListener('load', measure, { signal });
    target.addEventListener('load', measure, { signal });
    reduced.addEventListener('change', () => { cancelMotion(); needsMeasure = true; geometry = null; schedule(); }, { signal });
    document.fonts.ready.then(() => { if (!signal.aborted) measure(); });

    dispose = () => {
      controller.abort();
      resize.disconnect();
      state.disconnect();
      cancelAnimationFrame(frame);
      cancelMotion();
      morph.remove();
      ink.remove();
      header.style.removeProperty('--mr-glass-opacity');
      header.style.removeProperty('--mr-glass-blur');
      header.style.removeProperty('--mr-glass-saturation');
      root.classList.remove('mr-brand-ready', 'mr-brand-docked', 'mr-brand-reduced', 'mr-brand-navigation-open');
    };
    render();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.querySelector('[data-mr-header], [data-mr-brand-hero]')) dispose();
  });
})();
